import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import Icon from '@/components/Icon/Icon';
import { LabelXBase } from '@/components/LabelX';
import {
  DepartmentSelect,
  type Department,
} from '@/components/Select/DepartmentSelect';
import { apiClient } from '@/lib/api/client';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

type Props = CFormItemProps & {
  value?: string;
  allowEmpty?: boolean;
  readComponent?: ReactNode;
  onChange?: (departmentId?: string, department?: Department) => void;
  inputWidth?: number | string;
  inputClassName?: string;
  excludeIds?: string[];
  /**
   * 読み取り表示時に組織詳細への外部リンク（別タブ）を表示する。
   * 有効時はアイコンが建物アイコンから外部リンクアイコンに変わる。
   */
  showDetailLink?: boolean;
};

/**
 * Card-based Form / DepartmentSelectItem
 * 部署選択のためのCFormアイテム
 */
export const CFormDepartmentSelectItem = buildCFormItem<Props>(
  (props: Props) => {
    // 選択中の部署情報を取得
    const { data: selectedDepartment } = useQuery({
      queryKey: ['departmentDetail', props.value ?? ''],
      queryFn: async () => {
        if (!props.value) return null;

        const departmentRes = await apiClient.GET(
          '/v2/departments-for-select',
          {
            params: { query: { idIn: [props.value] } },
          },
        );

        // React Query v5ではundefinedを返すことができないため、nullを返す
        return departmentRes.data?.items[0] ?? null;
      },
      enabled: !!props.value,
    });

    const readComponent =
      props.readComponent ??
      (selectedDepartment ? (
        <LabelXBase label={selectedDepartment.name}>
          {props.showDetailLink ? (
            <Link
              to="/organization/$id"
              target="_blank"
              rel="noopener noreferrer"
              params={{ id: selectedDepartment.id }}
            >
              <Icon iconName="SquareArrowOutUpRightIcon" />
            </Link>
          ) : (
            <Icon iconName="Building2" className="shrink-0 w-6 h-6" />
          )}
        </LabelXBase>
      ) : null);

    return (
      <CFormItemBase
        {...props}
        readComponent={readComponent}
        writeComponent={
          <DepartmentSelect
            disabled={props.readonly}
            allowEmpty={props.allowEmpty}
            value={
              selectedDepartment
                ? {
                    id: selectedDepartment.id,
                    name: selectedDepartment.name,
                  }
                : undefined
            }
            onChange={(department) =>
              props.onChange?.(department?.id, department)
            }
            width={
              typeof props.inputWidth === 'number'
                ? props.inputWidth
                : undefined
            }
            excludeIds={props.excludeIds}
          />
        }
      />
    );
  },
);
